// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateProductCutsomerPartNumber from "@steiner/App/Handlers/ProductDetails/UpdateProductCutsomerPartNumber";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import * as React from "react";
import { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Change: Add state, dispatch
const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    updateProductCutsomerPartNumber,
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    // XNG-Change
    container?: InjectableCss;
    valueContainer?: InjectableCss;
    addCpnButton?: LinkPresentationProps;
    editForm?: InjectableCss;
    cpnField?: TextFieldPresentationProps;
    editCpnButton?: LinkPresentationProps;
}

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        size: 16,
        weight: "bold",
        css: css`
            margin-right: 15px;
        `,
    },
    valueText: {
        size: 16,
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = customerPartNumberStyles;


/**
 * XNG-Changes
 * STNRS-267
 * change condition to always show the widget regardless of product.customerProductNumber
 * show it only for Authenticated users
 * change structure
 * allow edit customer part number
 */
const ProductDetailsCustomerPartNumber: React.FC<Props> = ({
    product,
    // XNG-Changes
    isAuthenticated,
    updateProductCutsomerPartNumber,
}) => {
    const [isEditable, setIsEditable] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [cpn, setCpn] = useState(product.customerProductNumber);
    const [cpnField, setCpnField] = useState(product.customerProductNumber);

    React.useEffect(() => {
        setCpn(product.customerProductNumber);
        setCpnField(product.customerProductNumber);
    }, [product.customerProductNumber]);

    const toasterContext = useContext(ToasterContext);
    
    const updateCustomerNumber = () => {
        if (cpnField === cpn) {
            setIsEditable(false);
            return;
        }
        setIsUpdating(true);

        const type =
            !cpnField
            ? "delete"
            : cpn
            ? "edit"
            : "add";
        
        updateProductCutsomerPartNumber({
            productNumber: product.productNumber,
            customerPartNumber: cpnField || "",
            type,
            onSuccess: () => {
                toasterContext.addToast({
                    messageType: "success",
                    body: siteMessage(
                        type === "add"
                        ? "Custom_ProductDetails_CPN_Add_Success_Message"
                        : type === "edit"
                        ? "Custom_ProductDetails_CPN_Update_Success_Message"  
                        : "Custom_ProductDetails_CPN_Deleted_Success_Message"
                    ),
                });
                setIsUpdating(false);
                setIsEditable(false);
                setCpn(cpnField);
            },
            onError: (error: string) => {
                toasterContext.addToast({
                    messageType: "danger",
                    body: error || siteMessage("Custom_ProductDetails_CPN_Update_Error_Message"),
                });
                setIsUpdating(false);
            },
        });
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="customerProductNumber">
            {isEditable ? (
                <StyledWrapper {...styles.editForm}>
                    <TextField
                        disabled={isUpdating}
                        value={cpnField}
                        placeholder="0000"
                        onChange={e => setCpnField(e.target.value)}
                        maxLength={35}
                        {...styles.cpnField}
                        label={translate("Customer Part #:")}
                    />
                    <Button
                        disabled={isUpdating}
                        onClick={updateCustomerNumber}
                        aria-label="Add CPN"
                    >
                        { 
                            cpnField && cpn
                                ? translate("Update")
                                : cpn
                                ? translate("Delete")
                                : translate("Add")
                        }
                    </Button>
                </StyledWrapper>
            ) : cpn ? (
                <StyledWrapper {...styles.valueContainer}>
                    <Typography {...styles.labelText}>{translate("Customer Part #")}</Typography>
                    <Typography {...styles.valueText}>{cpn}</Typography>
                    <Link {...styles.editCpnButton} role="button" onClick={() => setIsEditable(true)}>{translate("Edit")}</Link>    
                </StyledWrapper>
            ) : (
                <Link {...styles.addCpnButton} role="button" onClick={() => setIsEditable(true)}>{translate("Add Customer Part #")}</Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsCustomerPartNumber)),
    definition: {
        displayName: "Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
